import { PropsWithChildren, useCallback } from 'react';

import * as css from './ToggleButtonCard.css';

/**
 * Props for {@link ToggleButtonCard}.
 *
 * @template T - The type of the value associated with the card.
 */
export interface ToggleButtonCardProps<T> extends PropsWithChildren {
    /**
     * Value associated with the card.
     * Returned to the parent when the card is clicked.
     */
    value: T;

    /**
     * Called when the card is clicked.
     * Receives the card's `value`.
     */
    onClick: (val: T) => void;

    /**
     * Controls whether the card is currently selected.
     */
    isSelected: boolean;

    /**
     * Optional badge number displayed inside the card.
     *
     * Typically used for step indexes or ordering.
     *
     * @default undefined
     */
    number?: number;
}

export function ToggleButtonCard<T>({ value, onClick, isSelected, number, children }: ToggleButtonCardProps<T>) {
    const handleOnClick = useCallback(() => {
        onClick(value);
    }, [onClick, value]);
    return (
        <button
            type="button"
            className={css.toggleButtonCard({
                selected: isSelected,
            })}
            onClick={handleOnClick}
        >
            {number ? <p className={css.toggleButtonCardNumber}>{number}</p> : null}
            {children}
        </button>
    );
}
